import React from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type { Task, TaskCreateData, TaskUpdateData, TaskStatus } from '../types/tasks';

// Paginated response type for tasks
interface TasksResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Task[];
}

/** Counts for a filtered set of tasks, taken from the database rather than the loaded rows. */
export interface TaskStatistics {
  total: number;
  overdue: number;
  by_status: Record<TaskStatus, number>;
}

/**
 * Every active status (excluding completed and cancelled), sent as `status__in` when the caller asks for
 * open tasks only. The list endpoint has no negative lookup, so naming the
 * wanted statuses is how the query says "open" — and it keeps the
 * exclusion on the server, where it applies to the whole table rather than to
 * whichever page happens to have loaded.
 */
const OPEN_TASK_STATUSES = ['not_started', 'in_progress', 'on_hold'].join(',');

/**
 * Ceiling on the rows one filter combination may accumulate.
 *
 * A "load more" list appends pages and nothing trims them until the filters
 * change, so without a stop it grows for as long as the user keeps clicking —
 * and every cached page is refetched whenever a task is edited. At the API page
 * size of 20 this is 25 pages deep, far past what anyone reads through; past it
 * the answer is a narrower filter, not more rows.
 */
export const MAX_LOADED_TASKS = 500;

/** Filters the task list and the task statistics endpoint both accept. */
export interface TaskQueryOptions {
  archived?: boolean;
  jobId?: string | number;
  status?: string;
  priority?: string;
  search?: string;
  /**
   * Restrict to one assignee. Applied by the server: filtering by assignee
   * after the fact can only ever narrow the page already in hand, which is how
   * "My Tasks" ends up empty for someone whose tasks sit further down.
   */
  assignedTo?: number;
  queue?: string;
  ordering?: string;
  dueFrom?: string;
  dueTo?: string;
  unlinked?: boolean;
}

/**
 * Translate the UI's filter state into query params.
 *
 * Shared by the list, the paged list, and the statistics summary so all three
 * describe the same set — a summary scoped differently from the rows beneath it
 * is worse than no summary.
 */
export const buildTaskListParams = (options: TaskQueryOptions): URLSearchParams => {
  const params = new URLSearchParams();
  if (options.archived) params.set("is_archived", "true");

  if (options.jobId) params.append('related_job', options.jobId.toString());
  if (options.assignedTo != null) params.append('assigned_to', options.assignedTo.toString());

  // Handle 'open' status to exclude completed tasks
  if (options.status === 'open') {
    params.append('status__in', OPEN_TASK_STATUSES);
  } else if (options.status && options.status !== 'all') {
    params.append('status', options.status);
  }

  if (options.priority && options.priority !== 'all') params.append('priority', options.priority);
  if (options.search) params.append('search', options.search);

  if (options.queue) params.set('queue', options.queue);
  if (options.dueFrom) params.set('due_date__gte', options.dueFrom);
  if (options.dueTo) params.set('due_date__lt', options.dueTo);
  if (options.unlinked) params.set('related_job__isnull', 'true');
  params.set('ordering', options.ordering || 'order_index,due_date,id');
  return params;
};

/** Normalise the two shapes the endpoint can answer with into the paginated one. */
const toPage = (data: TasksResponse | Task[]): TasksResponse =>
  Array.isArray(data)
    ? { count: data.length, next: null, previous: null, results: data }
    : { ...data, results: data.results || [] };

/**
 * Single page of tasks.
 *
 * Kept for callers that show a bounded set — the tasks of one job — where the
 * first page is the whole answer. Anything listing an organization's tasks
 * wants `useTasksInfiniteQuery`, which can reach past row 20.
 */
export const useTasksQuery = (options: TaskQueryOptions = {}) => {
  return useQuery<Task[]>({
    queryKey: ['tasks', options.jobId, options.status, options.priority, options.search, options.assignedTo],
    queryFn: async (): Promise<Task[]> => {
      const params = buildTaskListParams(options);
      const response = await apiClient.get<TasksResponse>(`/api/tasks/?${params}`);
      return toPage(response.data).results;
    },
  });
};

/**
 * The task list, page by page.
 *
 * The list endpoint paginates at 20, and the page that reads it used to take
 * the first page as the entire answer: no pager, no further requests, and
 * nothing on screen to say the other rows existed. This walks the pages on
 * demand and reports `totalCount` from the server, so the list can say how much
 * of the set it is showing.
 */
export const useTasksInfiniteQuery = (options: TaskQueryOptions = {}) => {
  const query = useInfiniteQuery<TasksResponse>({
    queryKey: [
      'tasks',
      'paged',
      options.jobId,
      options.status,
      options.priority,
      options.search,
      options.assignedTo, options.queue, options.ordering, options.dueFrom, options.dueTo, options.unlinked,
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<TasksResponse> => {
      const params = buildTaskListParams(options);
      params.append('page', String(pageParam));
      const response = await apiClient.get<TasksResponse>(`/api/tasks/?${params}`);
      return toPage(response.data);
    },
    getNextPageParam: (lastPage, allPages) => {
      // `next` is the server's own word on whether anything remains; trusting a
      // page's length instead miscounts the moment a page comes back short.
      if (!lastPage.next) return undefined;
      const loaded = allPages.reduce((total, page) => total + page.results.length, 0);
      return loaded >= MAX_LOADED_TASKS ? undefined : allPages.length + 1;
    },
  });

  const pages = query.data?.pages;

  const tasks = React.useMemo(
    () => pages?.flatMap((page) => page.results) ?? [],
    [pages],
  );

  // Every page reports the same total for a given filter, so the first is as
  // good as the last and is available a request earlier.
  const totalCount = pages?.[0]?.count ?? 0;

  return {
    ...query,
    tasks,
    totalCount,
    /** More rows match than are loaded, but the cap has stopped the list fetching them. */
    reachedLoadLimit: !query.hasNextPage && tasks.length >= MAX_LOADED_TASKS && tasks.length < totalCount,
  };
};

/**
 * Counts for the whole filtered set, from the server.
 *
 * The summary cards cannot be derived from the rows on screen: at a page size
 * of 20, "Total Tasks" read 20 for an organization with 200 and the other three
 * cards were wrong the same way. This asks the database, which counts the table.
 */
export const useTaskStatisticsQuery = (options: TaskQueryOptions = {}, enabled = true) => {
  return useQuery<TaskStatistics>({
    queryKey: [
      'tasks',
      'statistics',
      options.jobId,
      options.status,
      options.priority,
      options.search,
      options.assignedTo, options.queue, options.ordering, options.dueFrom, options.dueTo, options.unlinked,
    ],
    enabled,
    queryFn: async (): Promise<TaskStatistics> => {
      const params = buildTaskListParams(options);
      const response = await apiClient.get<TaskStatistics>(`/api/tasks/statistics/?${params}`);
      return response.data;
    },
  });
};

/**
 * Server-side count of the tasks assigned to a user.
 *
 * The My Tasks tab badge is most useful while you are looking at a *different*
 * tab, so it cannot be derived from the rows currently on screen. This asks the
 * API for the number directly and reads `count`, which describes the whole
 * table rather than the first page of it.
 */
export const useAssignedTaskCountQuery = (userId?: number) => {
  return useQuery<number>({
    queryKey: ['tasks', 'assignedCount', userId],
    enabled: userId != null,
    queryFn: async (): Promise<number> => {
      const params = new URLSearchParams({ assigned_to: String(userId) });
      const response = await apiClient.get<TasksResponse>(`/api/tasks/?${params}`);
      return Array.isArray(response.data) ? response.data.length : (response.data.count ?? 0);
    },
  });
};

// Hook for task mutations (create, update, delete, complete, start)
export const useTaskMutations = () => {
  const queryClient = useQueryClient();
  
  const invalidateTaskQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    queryClient.invalidateQueries({ queryKey: ['jobTasks'] });
  };

  const createTaskMutation = useMutation({
    mutationFn: async (data: TaskCreateData) => {
      const response = await apiClient.post<Task>('/api/tasks/', data);
      return response.data;
    },
    onSuccess: invalidateTaskQueries,
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, data }: { taskId: number; data: TaskUpdateData }) => {
      const response = await apiClient.patch(`/api/tasks/${taskId}/`, data);
      return response.data;
    },
    onSuccess: invalidateTaskQueries,
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (taskId: number) => {
      await apiClient.delete(`/api/tasks/${taskId}/`);
    },
    onSuccess: invalidateTaskQueries,
  });

  const completeTaskMutation = useMutation({
    mutationFn: async (taskId: number) => {
      await apiClient.post(`/api/tasks/${taskId}/complete/`, {});
    },
    onSuccess: invalidateTaskQueries,
  });

  const startTaskMutation = useMutation({
    mutationFn: async (taskId: number) => {
      await apiClient.post(`/api/tasks/${taskId}/start/`, {});
    },
    onSuccess: invalidateTaskQueries,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ taskId, status }: { taskId: number; status: TaskStatus }) => {
      const response = await apiClient.patch(`/api/tasks/${taskId}/`, { status });
      return response.data;
    },
    onSuccess: invalidateTaskQueries,
  });

  return {
    createTask: createTaskMutation,
    updateTask: updateTaskMutation,
    deleteTask: deleteTaskMutation,
    completeTask: completeTaskMutation,
    startTask: startTaskMutation,
    updateStatus: updateStatusMutation,
  };
};

// Hook for task filtering
export const useTaskFilters = (initialFilters: {
  status?: string;
  priority?: string;
  search?: string;
} = {}) => {
  const [filters, setFilters] = React.useState({
    status: 'open',
    priority: 'all',
    search: '',
    ...initialFilters,
  });

  const updateFilter = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      status: 'open',
      priority: 'all',
      search: '',
    });
  };

  return {
    filters,
    updateFilter,
    resetFilters,
  };
};